import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Users, BarChart3, CreditCard, Headphones, Megaphone } from "lucide-react";

const CHEF_URL = "https://images.pexels.com/photos/31566104/pexels-photo-31566104.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const benefits = [
  {
    icon: TrendingUp,
    title: "Aumenta Tus Ventas",
    description: "Llega a miles de clientes nuevos hambrientos y listos para ordenar.",
  },
  {
    icon: Users,
    title: "Más Visibilidad",
    description: "Tu negocio destacado en la app más popular de delivery de tu zona.",
  },
  {
    icon: BarChart3,
    title: "Panel de Control",
    description: "Estadísticas en tiempo real de ventas, pedidos y rendimiento.",
  },
  {
    icon: CreditCard,
    title: "Comisiones Justas",
    description: "Las tarifas más competitivas del mercado. Más ganancia para ti.",
  },
  {
    icon: Headphones,
    title: "Soporte 24/7",
    description: "Equipo dedicado para ayudarte a crecer tu negocio.",
  },
  {
    icon: Megaphone,
    title: "Marketing Incluido",
    description: "Promociones destacadas y campañas para atraer más clientes.",
  },
];

const BusinessBenefits = () => {
  return (
    <section id="negocios" className="py-24 dark-section" data-testid="business-benefits-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 order-2 lg:order-1"
          >
            {/* Section Header */}
            <div className="mb-10">
              <span className="uppercase text-xs tracking-[0.2em] font-bold text-primary">
                Para Tu Negocio
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mt-4">
                Haz Crecer Tu Restaurante Con Apedir
              </h2>
              <p className="text-base md:text-lg text-white/70 mt-4">
                Únete a la red de restaurantes más grande. Tecnología simple, 
                resultados potentes. Tu éxito es nuestro objetivo.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="group flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors"
                  data-testid={`business-benefit-${index + 1}`}
                >
                  <div className="p-2 rounded-lg bg-primary/20 group-hover:bg-primary/30 transition-colors shrink-0">
                    <benefit.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-white/60">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="mt-10"
            >
              <a
                href="#registro"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-4 font-bold transition-all duration-300 hover:scale-105 hover:shadow-glow"
                data-testid="business-cta-button"
              >
                Registrar Mi Negocio
                <span className="ml-1">→</span>
              </a>
            </motion.div>
          </motion.div>

          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 order-1 lg:order-2"
          >
            <div className="relative">
              <img
                src={CHEF_URL}
                alt="Chef feliz en cocina moderna"
                className="rounded-3xl w-full h-[500px] object-cover shadow-2xl"
                data-testid="business-benefits-image"
              />
              
              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl"
                animate={{
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-foreground">+45%</p>
                    <p className="text-xs text-muted-foreground">Aumento promedio en ventas</p>
                  </div>
                </div>
              </motion.div>

              {/* Background Glow */}
              <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary/20 rounded-full blur-3xl" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BusinessBenefits;
